// Static registry of "Claude AI Developer" (CD01) course lessons.
// The only authored data per lesson; everything else (body, exercise, canvas,
// rubric, test cases) is generated by Claude on first visit and cached in
// Firestore — mirrors pages/claudeCourse/lessonsRegistry.js.
//
// `layout` decides which lesson layout renders (UI only):
//   "canvas"    — Module 1: Lesson | (Concept Canvas + Prompt Lab).
//   "promptLab" — Modules 2/4/5: Lesson | Prompt Lab | Editor or Rubric panel.
//   "editor"    — Module 3: Lesson | Editor | Console (slim "Ask Claude").
//
// `grading` decides how the lesson is assessed (authoritative; decoupled from layout):
//   "canvas" — interactive self-check, no AI grade.
//   "rubric" — the student's conversation is graded against a rubric by an AI judge.
//   "tests"  — the student's code is run against deterministic stdin/stdout test cases.
//
// `lessonID` MUST match the Firestore `lessons` document ID — it keys the
// per-user content cache.
//
// DEF_VERSION: bump whenever the generated lesson schema or grading changes, so
// per-user cached definitions are treated as stale and regenerated on next load.
export const DEF_VERSION = 2;

export const CLAUDE_DEV_COURSE_LESSONS = [
    // Module 1 — Foundations: How Software Actually Works (interactive canvas self-check)
    { lessonID: "CD101", route: "CD_M1_L1", title: "What software is and what a developer actually does", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "CD102", route: "CD_M1_L2", title: "How a program runs (input → logic → output)", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "CD103", route: "CD_M1_L3", title: "Frontend vs. backend vs. database — the three-part picture", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "CD104", route: "CD_M1_L4", title: "What a \"programming language\" is and why there are many", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "CD105", route: "CD_M1_L5", title: "The developer's toolkit: editor, terminal, browser", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "CD106", route: "CD_M1_L6", title: "The single most important skill: breaking a problem into steps", layout: "canvas", type: "concept", grading: "canvas" },

    // Module 2 — Working With Claude as a Developer (conversation graded by rubric)
    { lessonID: "CD201", route: "CD_M2_L1", title: "What Claude can and can't do for a developer", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD202", route: "CD_M2_L2", title: "Anatomy of a good coding prompt (context, goal, constraints)", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD203", route: "CD_M2_L3", title: "Reading Claude's response: code, explanation, and assumptions", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD204", route: "CD_M2_L4", title: "Iterating: \"that's close, now change this\"", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD205", route: "CD_M2_L5", title: "Giving Claude an error message and getting unstuck", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD206", route: "CD_M2_L6", title: "Asking Claude to explain code you don't understand", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD207", route: "CD_M2_L7", title: "When to trust the AI and when to verify", layout: "promptLab", type: "concept", grading: "rubric" },

    // Module 3 — Programming Concepts You Must Recognize (editor, deterministic tests)
    { lessonID: "CD301", route: "CD_M3_L1", title: "Variables and data (text, numbers, true/false, lists)", layout: "editor", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD302", route: "CD_M3_L2", title: "Logic: if / else and making decisions", layout: "editor", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD303", route: "CD_M3_L3", title: "Loops: doing something repeatedly", layout: "editor", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD304", route: "CD_M3_L4", title: "Functions: reusable blocks of work", layout: "editor", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD305", route: "CD_M3_L5", title: "Reading an error message without panicking", layout: "editor", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD306", route: "CD_M3_L6", title: "What \"running\" code means and where it runs", layout: "editor", type: "code", language: "python", grading: "tests" },

    // Module 4 — Building a Real Project With Claude (planning → rubric, build → tests)
    { lessonID: "CD401", route: "CD_M4_L1", title: "Choosing a project and describing it to Claude", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD402", route: "CD_M4_L2", title: "Planning the pieces before writing any code", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD403", route: "CD_M4_L3", title: "Building the interface (the part users see)", layout: "promptLab", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD404", route: "CD_M4_L4", title: "Adding the logic (the part that does the work)", layout: "promptLab", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD405", route: "CD_M4_L5", title: "Connecting to data (saving and loading)", layout: "promptLab", type: "code", language: "python", grading: "tests" },
    { lessonID: "CD406", route: "CD_M4_L6", title: "Testing each piece as you go", layout: "promptLab", type: "concept", grading: "rubric" },

    // Module 5 — Shipping, Debugging & Growing (reflection / process graded by rubric)
    { lessonID: "CD501", route: "CD_M5_L1", title: "Version control basics with Git (using Claude to learn it)", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD502", route: "CD_M5_L2", title: "When things break: a calm debugging workflow", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD503", route: "CD_M5_L3", title: "Putting your app online so others can use it", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD504", route: "CD_M5_L4", title: "Reading documentation with AI as your translator", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "CD505", route: "CD_M5_L5", title: "Where to go next and how to keep learning", layout: "promptLab", type: "concept", grading: "rubric" },
];

// Every lesson carries the course's DEF_VERSION so consumers can check a cached
// definition against the version of the course it belongs to. PromptLabHubComponent
// is shared by CD01 and CD02, which version independently — it must not assume
// either course's constant.
CLAUDE_DEV_COURSE_LESSONS.forEach((l) => {
    l.defVersion = DEF_VERSION;
});

export const getLessonMetaByRoute = (route) =>
    CLAUDE_DEV_COURSE_LESSONS.find((l) => l.route === route);

/**
 * Given a route, return the route of the next lesson in the registry, or null
 * if this is the last one.
 */
export const getNextLessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx === -1 || idx === CLAUDE_DEV_COURSE_LESSONS.length - 1) return null;
    return CLAUDE_DEV_COURSE_LESSONS[idx + 1].route;
};

/**
 * Given a route, return the route of the previous lesson in the registry, or null
 * if this is the first one (the footer's Back button then falls back to the ToC).
 */
export const getPrevLessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx <= 0) return null;
    return CLAUDE_DEV_COURSE_LESSONS[idx - 1].route;
};

/**
 * Position of a lesson within its module, for a "Lesson X of Y" progress label.
 * The module number is parsed from the CD_M{n}_L{k} route and the total counts
 * lessons sharing that module. Returns null for unknown/non-CD routes.
 */
export const getLessonProgress = (route) => {
    const m = /^CD_M(\d+)_L/.exec(route || "");
    if (!m) return null;
    const moduleNum = Number(m[1]);
    const moduleLessons = CLAUDE_DEV_COURSE_LESSONS.filter((l) =>
        l.route.startsWith(`CD_M${moduleNum}_L`)
    );
    const lessonInModule = moduleLessons.findIndex((l) => l.route === route) + 1;
    if (lessonInModule === 0) return null;
    return { moduleNum, lessonInModule, totalInModule: moduleLessons.length };
};
